import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { formatDuration } from '../utils/challenge';

const getComparisonCopy = (sourceResult, result) => {
  if (!sourceResult) return null;

  const delta = result.score - sourceResult.score;
  if (delta > 0) return `You beat the original score by ${delta}.`;
  if (delta === 0) return 'You matched the original score.';
  if (Math.abs(delta) <= 2) return `Close game. You were ${Math.abs(delta)} away.`;
  return `You were ${Math.abs(delta)} behind the original score.`;
};

export default function ChallengeSummary({
  challenge,
  result,
  onReplay,
  onExit,
  onShareChallenge,
  onShareResult,
  shareMessage,
}) {
  const { colors } = useTheme();
  const comparisonCopy = getComparisonCopy(challenge?.sourceResult, result);

  const styles = {
    container: {
      minHeight: '100vh',
      background: colors.backgroundAccent,
      color: colors.text,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 16px',
      boxSizing: 'border-box',
    },
    card: {
      width: 'min(520px, 100%)',
      backgroundColor: colors.surface,
      borderRadius: '28px',
      padding: '28px 24px',
      boxShadow: colors.shadow,
      border: `1px solid ${colors.border}`,
      textAlign: 'center',
    },
    eyebrow: {
      margin: '0 0 8px',
      fontSize: '12px',
      textTransform: 'uppercase',
      letterSpacing: '0.16em',
      opacity: 0.7,
    },
    title: {
      margin: '0 0 12px',
      fontSize: '32px',
      lineHeight: 1.1,
    },
    subtitle: {
      margin: '0 0 20px',
      opacity: 0.8,
      lineHeight: 1.5,
    },
    score: {
      fontSize: '72px',
      fontWeight: '800',
      color: result.score >= 0 ? colors.positiveScore : colors.negativeScore,
      lineHeight: 1,
      margin: '8px 0 18px',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '12px',
      marginBottom: '20px',
    },
    stat: {
      backgroundColor: colors.button,
      color: colors.buttonText,
      borderRadius: '18px',
      padding: '14px 12px',
    },
    statLabel: {
      display: 'block',
      fontSize: '12px',
      opacity: 0.7,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      marginBottom: '6px',
    },
    statValue: {
      fontSize: '22px',
      fontWeight: '700',
    },
    compare: {
      margin: '0 0 18px',
      padding: '12px 14px',
      borderRadius: '16px',
      backgroundColor: colors.button,
      color: colors.buttonText,
      fontWeight: '600',
    },
    resultCard: {
      marginBottom: '20px',
      padding: '18px',
      borderRadius: '22px',
      background: `linear-gradient(135deg, ${colors.surface} 0%, ${colors.button} 100%)`,
      border: `1px solid ${colors.border}`,
      textAlign: 'left',
    },
    resultHeader: {
      fontSize: '13px',
      textTransform: 'uppercase',
      letterSpacing: '0.12em',
      opacity: 0.7,
      marginBottom: '10px',
    },
    resultTitle: {
      fontSize: '24px',
      fontWeight: '800',
      marginBottom: '8px',
    },
    resultMeta: {
      display: 'flex',
      gap: '10px',
      flexWrap: 'wrap',
      fontSize: '14px',
      opacity: 0.85,
      marginBottom: '10px',
    },
    beatCopy: {
      fontSize: '18px',
      fontWeight: '700',
      lineHeight: 1.3,
    },
    buttonRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '12px',
      marginBottom: '12px',
    },
    button: {
      border: 'none',
      borderRadius: '999px',
      backgroundColor: colors.button,
      color: colors.buttonText,
      padding: '13px 16px',
      cursor: 'pointer',
      fontSize: '15px',
      fontWeight: '700',
    },
    primaryButton: {
      backgroundColor: colors.accent,
      color: colors.accentText,
    },
    footerRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '12px',
    },
    shareMessage: {
      marginTop: '12px',
      fontSize: '13px',
      opacity: 0.75,
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <p style={styles.eyebrow}>Challenge Complete</p>
        <h1 style={styles.title}>{challenge.title}</h1>
        <p style={styles.subtitle}>
          {challenge.topic} • {challenge.difficulty} • {result.totalCards} cards
        </p>

        <div style={styles.score}>{result.score}</div>

        <div style={styles.grid}>
          <div style={styles.stat}>
            <span style={styles.statLabel}>Accuracy</span>
            <span style={styles.statValue}>{result.accuracy}%</span>
          </div>
          <div style={styles.stat}>
            <span style={styles.statLabel}>Best Streak</span>
            <span style={styles.statValue}>{result.bestStreak}</span>
          </div>
          <div style={styles.stat}>
            <span style={styles.statLabel}>Time</span>
            <span style={styles.statValue}>{formatDuration(result.durationMs)}</span>
          </div>
          <div style={styles.stat}>
            <span style={styles.statLabel}>Result</span>
            <span style={styles.statValue}>{result.correct}/{result.totalCards}</span>
          </div>
        </div>

        {comparisonCopy && <div style={styles.compare}>{comparisonCopy}</div>}

        <div style={styles.resultCard}>
          <div style={styles.resultHeader}>Share this result</div>
          <div style={styles.resultTitle}>{challenge.title}</div>
          <div style={styles.resultMeta}>
            <span>Score {result.score}</span>
            <span>Accuracy {result.accuracy}%</span>
            <span>Time {formatDuration(result.durationMs)}</span>
            <span>Streak {result.bestStreak}</span>
          </div>
          <div style={styles.beatCopy}>Beat this.</div>
        </div>

        <div style={styles.buttonRow}>
          <button onClick={onShareResult} style={{ ...styles.button, ...styles.primaryButton }}>
            Share Result
          </button>
          <button onClick={onShareChallenge} style={styles.button}>
            Share Challenge
          </button>
        </div>

        <div style={styles.footerRow}>
          <button onClick={onReplay} style={styles.button}>
            Replay
          </button>
          <button onClick={onExit} style={styles.button}>
            Exit Challenge
          </button>
        </div>

        {shareMessage && <div style={styles.shareMessage}>{shareMessage}</div>}
      </div>
    </div>
  );
}
